import React, { useEffect, useMemo, useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import api, { arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";

const STATUS_COLOR = {
  published: "bg-green-100 text-green-700",
  scheduled: "bg-blue-100 text-blue-700",
  draft: "bg-slate-100 text-slate-600",
  pending_approval: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
};
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function CalendarPage() {
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => {
    // Fetch up to 500 posts so the calendar shows across all months without re-fetching on navigation
    api.get("/posts", { params: { limit: 500, page: 1 } }).then(({ data }) => {
      setPosts(arr(data?.data));
    }).catch(() => setPosts([]));
  }, []);

  const days = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startPad = first.getDay();
    const total = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(year, month, d));
    return cells;
  }, [cursor]);

  const postsForDay = (date) => {
    if (!date) return [];
    return arr(posts).filter((p) => {
      const ref = p.scheduledAt || p.publishedAt;
      if (!ref) return false;
      return new Date(ref).toDateString() === date.toDateString();
    });
  };

  const shift = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Content Calendar"
        subtitle="Your scheduled and published posts"
        icon={CalendarIcon}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 hover:bg-slate-50">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[140px] text-center font-heading text-base font-semibold text-slate-800">
              {cursor.toLocaleString("en-US", { month: "long", year: "numeric" })}
            </span>
            <button onClick={() => shift(1)} className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 hover:bg-slate-50">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        }
      />

      <div className="card-surface overflow-hidden p-2">
        <div className="grid grid-cols-7 gap-1">
          {WEEKDAYS.map((w) => (
            <div key={w} className="py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">{w}</div>
          ))}
          {days.map((date, i) => (
            <div key={i} className={`min-h-[96px] rounded-xl border p-1.5 ${date ? "border-slate-100 bg-white" : "border-transparent"}`}>
              {date && (
                <>
                  <p className={`mb-1 text-xs font-semibold ${date.toDateString() === new Date().toDateString() ? "text-brand-600" : "text-slate-400"}`}>
                    {date.getDate()}
                  </p>
                  <div className="space-y-1">
                    {postsForDay(date).slice(0, 3).map((p) => (
                      <div
                        key={p.id || p._id}
                        className={`truncate rounded-md px-1.5 py-0.5 text-[10px] font-medium ${STATUS_COLOR[p.status] || "bg-slate-100 text-slate-600"}`}
                        title={p.title}
                      >
                        {p.title || "Post"}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
